import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExpensesSummary,
  selectAllExpenses,
  selectSelectedDay,
  selectStatus,
  selectFilterDays,
  setSelectedDay,
  setFilterDays,
  clearSelectedDay,
} from "../../RTK/ExpensesSummary/GetExpensesSummary";
import dayjs from "dayjs";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Activity,
  ChevronRight,
  Loader2,
  CreditCard,
  User,
  Plus,
  Lock,
  Wallet,
} from "lucide-react";
import CreateExpenseModal from "./popups/CreateExpenseModal";
import { toast } from "react-toastify";
import { usePermissions } from "../../contexts/PermissionsContext";

const ExpensesSummaryPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const canCreateExpense = hasPermission("create_expense");

  const expenses = useSelector(selectAllExpenses);
  const selectedDay = useSelector(selectSelectedDay);
  const status = useSelector(selectStatus);
  const filterDays = useSelector(selectFilterDays);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // حساب الإجمالي الكلي
  const totalSummary = useMemo(() => {
    return expenses.reduce(
      (acc, day) => ({
        totalCredit: acc.totalCredit + parseFloat(day.total_credit || 0),
        totalDebit: acc.totalDebit + parseFloat(day.total_debit || 0),
        netAmount: acc.netAmount + parseFloat(day.net_amount || 0),
        totalTransactions:
          acc.totalTransactions + parseInt(day.transactions_count || 0),
      }),
      { totalCredit: 0, totalDebit: 0, netAmount: 0, totalTransactions: 0 },
    );
  }, [expenses]);

  useEffect(() => {
    dispatch(fetchExpensesSummary(filterDays));
  }, [dispatch, filterDays]);

  const handleDaySelect = (day) => {
    if (selectedDay?.date === day.date) {
      dispatch(clearSelectedDay());
    } else {
      dispatch(setSelectedDay(day));
    }
  };

  const handleCreateExpense = () => {
    if (!canCreateExpense) {
      toast.error("ليس لديك صلاحية لإنشاء المصروفات 🔒");
      return;
    }
    setShowCreateModal(true);
  };

  const filterOptions = [
    { label: "آخر 7 أيام", value: 7 },
    { label: "آخر 14 يوم", value: 14 },
    { label: "آخر 30 يوم", value: 30 },
  ];

  // ✅ كارد الإجمالي الكلي
  const TotalSummaryCard = () => {
    return (
      <div className="bg-gradient-to-br from-yellow-500/10 via-gray-900 to-gray-800 rounded-xl p-6 border-2 border-yellow-500/30 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <Wallet className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">الإجمالي الكلي</h3>
              <p className="text-sm text-gray-400">
                آخر {filterDays} يوم • {totalSummary.totalTransactions} معاملة
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {/* إيداع */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-emerald-500/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-emerald-400 font-medium">
                إجمالي الإيداع
              </span>
            </div>
            <p className="text-2xl font-bold text-white">
              ${totalSummary.totalCredit.toFixed(2)}
            </p>
          </div>

          {/* سحب */}
          <div className="bg-gray-800/50 rounded-lg p-4 border border-rose-500/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-rose-500" />
              <span className="text-sm text-rose-400 font-medium">
                إجمالي السحب
              </span>
            </div>
            <p className="text-2xl font-bold text-white">
              ${totalSummary.totalDebit.toFixed(2)}
            </p>
          </div>

          {/* الصافي */}
          <div
            className={`bg-gray-800/50 rounded-lg p-4 border ${
              totalSummary.netAmount >= 0
                ? "border-yellow-500/20"
                : "border-orange-500/20"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign
                className={`w-5 h-5 ${
                  totalSummary.netAmount >= 0
                    ? "text-yellow-500"
                    : "text-orange-500"
                }`}
              />
              <span
                className={`text-sm font-medium ${
                  totalSummary.netAmount >= 0
                    ? "text-yellow-400"
                    : "text-orange-400"
                }`}
              >
                الصافي الكلي
              </span>
            </div>
            <p
              className={`text-2xl font-bold ${
                totalSummary.netAmount >= 0
                  ? "text-yellow-500"
                  : "text-orange-500"
              }`}
            >
              {totalSummary.netAmount >= 0 ? "+" : ""}$
              {totalSummary.netAmount.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const DaySummaryCard = ({ day }) => {
    const isSelected = selectedDay?.date === day.date;
    return (
      <div
        onClick={() => handleDaySelect(day)}
        className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-4 cursor-pointer transition-all duration-300 border border-gray-800
          ${
            isSelected
              ? "ring-2 ring-yellow-500 border-transparent"
              : "hover:border-yellow-500/20"
          }`}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-400">
            {dayjs(day.date).format("DD MMM, YYYY")}
          </span>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-400">
              {day.transactions_count} معاملة
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-emerald-500">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">إيداع</span>
            </div>
            <p className="text-lg font-semibold text-white">
              ${day.total_credit}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1 text-rose-500">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs">سحب</span>
            </div>
            <p className="text-lg font-semibold text-white">
              ${day.total_debit}
            </p>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1 text-yellow-500">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">الصافي</span>
            </div>
            <p className="text-lg font-semibold text-white">
              ${day.net_amount}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const DayDetails = ({ day }) => {
    return (
      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-yellow-500" />
            تفاصيل {dayjs(day.date).format("DD MMM, YYYY")}
          </h3>
          <button
            onClick={() => dispatch(clearSelectedDay())}
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
          >
            <ChevronRight className="w-4 h-4" />
            عودة للملخص
          </button>
        </div>

        {day.payment_method_statistics.map((method, index) => (
          <div key={index} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-yellow-500" />
                <h4 className="font-medium text-white">
                  {method.payment_method}
                </h4>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-400">
                  {method.transactions_count} معاملة
                </p>
                <p className="text-sm font-medium text-white">
                  ${method.total_amount}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {method.transaction_details.map((transaction, idx) => (
                <div
                  key={idx}
                  className="bg-gray-800/50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="space-y-1">
                    <p className="text-sm text-white">{transaction.cause}</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <p className="text-xs text-gray-400">
                        {transaction.created_by}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`text-lg font-medium ${
                      transaction.transaction_type === "debit"
                        ? "text-rose-500"
                        : "text-emerald-500"
                    }`}
                  >
                    {transaction.transaction_type === "debit" ? "-" : "+"}$
                    {transaction.amount}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mt-8 sm:mt-4">
        <h1 className="text-lg sm:text-2xl font-bold text-white">
          ملخص المصروفات
        </h1>

        {canCreateExpense ? (
          <button
            onClick={handleCreateExpense}
            className="px-4 py-2 rounded-lg bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">إنشاء مصروف</span>
          </button>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
            <Lock className="w-4 h-4 text-slate-500" />
            <span className="text-slate-400 text-sm hidden sm:inline">
              ليس لديك صلاحية إنشاء المصروفات
            </span>
            <Lock className="w-4 h-4 text-slate-500 sm:hidden" />
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center justify-center gap-3 mt-8 sm:mt-4">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => dispatch(setFilterDays(option.value))}
            className={`px-4 py-2 rounded-lg text-sm transition-colors
          ${
            filterDays === option.value
              ? "bg-yellow-500 text-gray-900"
              : "bg-gray-800 text-gray-400 hover:text-yellow-500"
          }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* ✅ كارد الإجمالي الكلي */}
      {!selectedDay && expenses.length > 0 && <TotalSummaryCard />}

      {/* Content */}
      <div className="space-y-6">
        {!selectedDay ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {expenses.map((day, index) => (
              <DaySummaryCard key={index} day={day} />
            ))}
          </div>
        ) : (
          <DayDetails day={selectedDay} />
        )}
      </div>

      {showCreateModal && canCreateExpense && (
        <CreateExpenseModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            dispatch(fetchExpensesSummary(filterDays));
          }}
        />
      )}
    </div>
  );
};

export default ExpensesSummaryPage;
